/**
 * S3 Upload Hook
 * Handles file uploads to S3 via backend proxy endpoint
 */

import { useState, useCallback, useRef } from 'react';
import { useAccessToken } from '../auth/hooks';
import { getAuthConfig } from '../config/auth';

export interface S3UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
  uploadedUrl: string | null;
}

export interface S3UploadActions {
  upload: (file: File, streamerName: string, fileDate: string) => Promise<string | null>;
  reset: () => void;
  cancel: () => void;
}

export interface UseS3UploadResult extends S3UploadState, S3UploadActions {}

interface FileUploadResponse {
  file_key: string;
  bucket: string;
  s3_url: string;
}

/**
 * Get the API URL (same logic as client.ts)
 */
function getApiUrl(): string {
  const config = getAuthConfig();

  if (import.meta.env.VITE_AUTHORITY) {
    // Deployed environment
    const url = config.apiUrl;
    if (url.startsWith('http://') && !url.includes('localhost')) {
      return url.replace('http://', 'https://');
    }
    return url;
  }

  if (import.meta.env.VITE_ENVIRONMENT === 'dev' && import.meta.env.VITE_DEV_API_URL) {
    const url = import.meta.env.VITE_DEV_API_URL;
    if (url.startsWith('http://') && !url.includes('localhost')) {
      return url.replace('http://', 'https://');
    }
    return url;
  }

  return 'http://localhost:8000';
}

/**
 * Get dev API token for local dev environments
 */
let devApiToken: string | null = null;
let devApiTokenExpiry: number = 0;

async function getDevApiToken(): Promise<string | null> {
  if (!import.meta.env.VITE_ENVIRONMENT || import.meta.env.VITE_ENVIRONMENT !== 'dev') {
    return null;
  }
  if (import.meta.env.VITE_AUTHORITY) {
    return null; // Deployed, use OIDC
  }

  if (devApiToken && Date.now() < devApiTokenExpiry - 60000) {
    return devApiToken;
  }

  const user = import.meta.env.VITE_DEV_API_USER;
  const password = import.meta.env.VITE_DEV_API_PASSWORD;
  const apiUrl = import.meta.env.VITE_DEV_API_URL;

  if (!user || !password || !apiUrl) return null;

  try {
    const basicAuth = btoa(`${user}:${password}`);
    const response = await fetch(`${apiUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${basicAuth}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ username: user, password }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    devApiToken = data.access_token;
    devApiTokenExpiry = Date.now() + (data.expires_in || 1800) * 1000;
    return devApiToken;
  } catch {
    return null;
  }
}

/**
 * Hook for uploading files to S3 via backend with progress tracking
 */
export function useS3Upload(): UseS3UploadResult {
  const token = useAccessToken();
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const [state, setState] = useState<S3UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
    uploadedUrl: null,
  });

  const cancel = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isUploading: false,
      error: 'Upload cancelled',
    }));
  }, []);

  const reset = useCallback(() => {
    cancel();
    setState({
      isUploading: false,
      progress: 0,
      error: null,
      uploadedUrl: null,
    });
  }, [cancel]);

  const upload = useCallback(async (file: File, streamerName: string, fileDate: string): Promise<string | null> => {
    setState({
      isUploading: true,
      progress: 0,
      error: null,
      uploadedUrl: null,
    });

    try {
      const apiUrl = getApiUrl();

      // Get the appropriate auth token
      let authToken = token;
      const devToken = await getDevApiToken();
      if (devToken) {
        authToken = devToken;
      }

      // Build multipart form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('streamer_name', streamerName);
      formData.append('file_date', fileDate);

      // Use XHR for progress tracking
      return new Promise<string | null>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            setState((prev) => ({ ...prev, progress }));
          }
        });

        xhr.addEventListener('load', () => {
          xhrRef.current = null;
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response: FileUploadResponse = JSON.parse(xhr.responseText);
              setState((prev) => ({
                ...prev,
                isUploading: false,
                progress: 100,
                uploadedUrl: response.s3_url,
              }));
              resolve(response.s3_url);
            } catch {
              const error = 'Failed to parse upload response';
              setState((prev) => ({ ...prev, isUploading: false, error }));
              reject(new Error(error));
            }
          } else if (xhr.status === 401 || xhr.status === 403) {
            const error = 'Authentication failed. Please refresh and try again.';
            setState((prev) => ({ ...prev, isUploading: false, error }));
            reject(new Error(error));
          } else if (xhr.status === 413) {
            const error = 'File too large. Maximum size is 500MB.';
            setState((prev) => ({ ...prev, isUploading: false, error }));
            reject(new Error(error));
          } else {
            const error = `Upload failed with status ${xhr.status}`;
            setState((prev) => ({ ...prev, isUploading: false, error }));
            reject(new Error(error));
          }
        });

        xhr.addEventListener('error', () => {
          xhrRef.current = null;
          const error = 'Upload failed due to network error';
          setState((prev) => ({ ...prev, isUploading: false, error }));
          reject(new Error(error));
        });

        xhr.addEventListener('abort', () => {
          xhrRef.current = null;
          setState((prev) => ({ ...prev, isUploading: false, error: 'Upload cancelled' }));
          resolve(null);
        });

        xhr.open('POST', `${apiUrl}/echo/s3-upload`);
        if (authToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        }
        // Don't set Content-Type — browser sets it with multipart boundary
        xhr.send(formData);
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to upload file';
      setState((prev) => ({ ...prev, isUploading: false, error }));
      return null;
    }
  }, [token]);

  return {
    ...state,
    upload,
    reset,
    cancel,
  };
}

export default useS3Upload;
